export const ENROLLMENT_AUTOMATION_FAMILIES = ['enrollment', 'statusCheck'] as const;
export type EnrollmentAutomationFamily = (typeof ENROLLMENT_AUTOMATION_FAMILIES)[number];

// The fixed Statsig user each family evaluates its configs against.
export const ENROLLMENT_AUTOMATION_STATSIG_USER_IDS = {
  enrollment: 'provider-enrollments',
  statusCheck: 'provider-enrollment-status-checks',
} as const satisfies Record<EnrollmentAutomationFamily, string>;

export type EnrollmentAutomationRunTable = 'ProviderEnrollmentRun' | 'ProviderEnrollmentStatusCheckRun';

export interface EnrollmentAutomation {
  // The Statsig dynamic config name. Also the lookup key for the board.
  configName: string;
  payerLabel: string;
  family: EnrollmentAutomationFamily;
  // False means a SHADOW dispatch is refused, not honored (Aetna and UHC enrollment).
  supportsShadowMode: boolean;
  // False means the automated path has no `enabled` gate (BCBS NC enrollment runs from a cron).
  enabledGated: boolean;
  runTable: EnrollmentAutomationRunTable;
  // The payerKey this workflow writes to its run rows. The two families use different alphabets:
  // enrollment is camelCase and unique per payer; status checks share `bcbs`/`regence` across states.
  runTablePayerKey: string;
  // Set only when the payerKey is shared across states, so the health query must scope by state
  // (BCBS and Regence status checks). Everywhere else the payerKey alone identifies the workflow,
  // and the run rows carry a per-provider state that must not be used as a filter.
  runTablePayerState?: string;
}

// Verified against the recipes and registries on 2026-10-27. Sources: the enrollment recipes under
// server/src/services/providerEnrollments/recipes/ and providerEnrollmentWorkflowRegistry.ts; the
// status-check configs in statusCheckConfigs.ts and the *StatusCheckWorkflow.ts recipes.
export const ENROLLMENT_AUTOMATIONS: readonly EnrollmentAutomation[] = [
  // ── Enrollment (9) ──
  {
    configName: 'provider_enrollment_aetna_enrollments',
    payerLabel: 'Aetna',
    family: 'enrollment',
    supportsShadowMode: false,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'aetna',
  },
  {
    configName: 'provider_enrollment_united_healthcare_enrollments',
    payerLabel: 'United Healthcare',
    family: 'enrollment',
    supportsShadowMode: false,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'unitedHealthcare',
  },
  {
    configName: 'provider_enrollment_highmark_pa_enrollments',
    payerLabel: 'Highmark PA',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'highmark',
  },
  {
    configName: 'provider_enrollment_independence_pa_enrollments',
    payerLabel: 'Independence PA',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'independencePa',
  },
  {
    configName: 'provider_enrollment_bcbs_ma_enrollments',
    payerLabel: 'BCBS MA',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'bcbsMa',
  },
  {
    configName: 'provider_enrollment_bcbs_tx_enrollments',
    payerLabel: 'BCBS TX',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'bcbsTx',
  },
  {
    configName: 'provider_enrollment_bcbs_al_enrollments',
    payerLabel: 'BCBS AL',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'bcbsAl',
  },
  {
    configName: 'provider_enrollment_bcbs_il_enrollments',
    payerLabel: 'BCBS IL',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'bcbsIl',
  },
  {
    configName: 'provider_enrollment_bcbs_nc_enrollments',
    payerLabel: 'BCBS NC',
    family: 'enrollment',
    supportsShadowMode: true,
    enabledGated: false,
    runTable: 'ProviderEnrollmentRun',
    runTablePayerKey: 'bcbsNc',
  },

  // ── Status checks (12) ──
  {
    configName: 'provider_enrollment_bcbs_il_status_checks',
    payerLabel: 'BCBS IL',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'bcbs',
    runTablePayerState: 'IL',
  },
  {
    configName: 'provider_enrollment_bcbs_mt_status_checks',
    payerLabel: 'BCBS MT',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'bcbs',
    runTablePayerState: 'MT',
  },
  {
    configName: 'provider_enrollment_bcbs_tx_status_checks',
    payerLabel: 'BCBS TX',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'bcbs',
    runTablePayerState: 'TX',
  },
  {
    configName: 'provider_enrollment_regence_or_status_checks',
    payerLabel: 'Regence OR',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'regence',
    runTablePayerState: 'OR',
  },
  {
    configName: 'provider_enrollment_regence_ut_status_checks',
    payerLabel: 'Regence UT',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'regence',
    runTablePayerState: 'UT',
  },
  {
    configName: 'provider_enrollment_regence_id_status_checks',
    payerLabel: 'Regence ID',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'regence',
    runTablePayerState: 'ID',
  },
  {
    configName: 'provider_enrollment_uhc_status_checks',
    payerLabel: 'United Healthcare',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'united-healthcare',
  },
  {
    configName: 'provider_enrollment_anthem_nh_ny_status_checks',
    payerLabel: 'Anthem NH/NY',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'anthem',
  },
  {
    configName: 'provider_enrollment_ibc_pa_status_checks',
    payerLabel: 'IBC PA',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'ibc_pa',
  },
  {
    configName: 'provider_enrollment_bcbs_al_status_checks',
    payerLabel: 'BCBS AL',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'bcbs_al',
  },
  {
    configName: 'provider_enrollment_bcbs_ma_status_checks',
    payerLabel: 'BCBS MA',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'bcbs_ma',
  },
  {
    configName: 'provider_enrollment_highmark_pa_status_checks',
    payerLabel: 'Highmark PA',
    family: 'statusCheck',
    supportsShadowMode: true,
    enabledGated: true,
    runTable: 'ProviderEnrollmentStatusCheckRun',
    runTablePayerKey: 'highmark',
  },
];

// The Statsig console project that holds these dynamic configs (same project as the patient banner).
const STATSIG_CONSOLE_DYNAMIC_CONFIG_URL_PREFIX = 'https://console.statsig.com/6RFekk7eSu2Gu59L5p04xx/dynamic_configs/';

export function enrollmentAutomationStatsigUrl(configName: string): string {
  return `${STATSIG_CONSOLE_DYNAMIC_CONFIG_URL_PREFIX}${configName}`;
}

export function deriveEnrollmentAutomationEnabled(config: Record<string, unknown> | null | undefined): boolean {
  return config?.enabled === true;
}

// Enrollment: shadow is ON unless the config sets it false. Status check: shadow is OFF unless the
// config sets it true. Mirrors enrollmentDynamicConfig.ts and statusCheckDynamicConfig.ts; the two
// families read the same field with opposite defaults.
export function deriveEnrollmentAutomationShadowMode(
  family: EnrollmentAutomationFamily,
  config: Record<string, unknown> | null | undefined
): boolean {
  if (family === 'enrollment') {
    return config?.shadowMode !== false;
  }
  return config?.shadowMode === true;
}
